'use client';

import type {
  FullRescanFindingSummary,
  FullRescanRun,
} from '@terra/shared-contracts-applications';
import { AlertBanner } from '../../../../components/alert-banner';
import {
  Badge,
  BadgeSeverity,
  isBadgeSeverityType,
} from '../../../../components/badge';
import {
  BodyDefault,
  LabelDefault,
  MetaDefault,
} from '../../../../components/typography';
import { HStack, VStack } from '@terra/ui-styled-system';
import { useFormatter, useTranslations } from 'next-intl';

const PREVIEW_LIMIT = 5;

export type FullRescanStatusProps = {
  run: FullRescanRun;
};

const FindingPreviewList = ({
  title,
  findings,
  total,
}: {
  title: string;
  findings: FullRescanFindingSummary[];
  total: number;
}) => {
  const t = useTranslations('designSystem.scanEffort.fullRescan.diff');

  if (total === 0) {
    return null;
  }

  return (
    <VStack alignItems="stretch" gap={1}>
      <LabelDefault>{title}</LabelDefault>
      {findings.slice(0, PREVIEW_LIMIT).map((finding) => (
        <HStack key={finding.findingId} gap={2}>
          {isBadgeSeverityType(finding.severity) ? (
            <BadgeSeverity severity={finding.severity} size="sm">
              {finding.severity}
            </BadgeSeverity>
          ) : null}
          <BodyDefault truncate>{finding.title}</BodyDefault>
        </HStack>
      ))}
      {total > PREVIEW_LIMIT ? (
        <MetaDefault color="fg.muted">
          {t('more', { count: total - PREVIEW_LIMIT })}
        </MetaDefault>
      ) : null}
    </VStack>
  );
};

export const FullRescanStatus = ({ run }: FullRescanStatusProps) => {
  const t = useTranslations('designSystem.scanEffort.fullRescan');
  const tEffort = useTranslations('designSystem.scanEffort');
  const format = useFormatter();
  const startedAt = format.dateTime(new Date(run.startedAt), {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  const failedSteps = run.steps.filter(({ status }) => status === 'FAILED');
  const { diff } = run;

  return (
    <VStack alignItems="stretch" gap={3}>
      <MetaDefault color="fg.muted">
        {run.active
          ? t('status.active', {
              startedAt,
              level: tEffort(`levels.${run.effortLevel}`),
            })
          : t('status.finished', { startedAt })}
      </MetaDefault>
      {run.active && run.steps.length === 0 ? (
        <MetaDefault color="fg.muted">{t('status.starting')}</MetaDefault>
      ) : null}
      {failedSteps.length > 0 ? (
        <AlertBanner
          kind="attention"
          description={t('status.stepsFailed', {
            steps: failedSteps.map(({ step }) => t(`steps.${step}`)).join(', '),
          })}
        />
      ) : null}
      <HStack gap={2} flexWrap="wrap">
        <Badge variant="outline" size="sm">
          {t('diff.newCount', { count: diff.newCount })}
        </Badge>
        <Badge variant="outline" size="sm">
          {t('diff.reconfirmedCount', { count: diff.reconfirmedCount })}
        </Badge>
        <Badge variant="outline" size="sm">
          {run.active
            ? t('diff.notReproducedYetCount', {
                count: diff.notReproducedCount,
              })
            : t('diff.notReproducedCount', {
                count: diff.notReproducedCount,
              })}
        </Badge>
      </HStack>
      <FindingPreviewList
        title={t('diff.newTitle')}
        findings={diff.newFindings}
        total={diff.newCount}
      />
      <FindingPreviewList
        title={t('diff.reconfirmedTitle')}
        findings={diff.reconfirmedFindings}
        total={diff.reconfirmedCount}
      />
      {!run.active ? (
        <FindingPreviewList
          title={t('diff.notReproducedTitle')}
          findings={diff.notReproducedFindings}
          total={diff.notReproducedCount}
        />
      ) : null}
      <MetaDefault color="fg.muted">{t('diff.dedupeHint')}</MetaDefault>
    </VStack>
  );
};
